import type { GridRowId } from '@mui/x-data-grid-pro';
import { useSectionData } from '../../../Sections/useSectionContext';
import { useTableContentUndoRedo } from '../../../hooks/useTableContentUndoRedo.ts';
import { useTableManipulation } from '../../Block/Table/useTableManipulation';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useActiveEditor } from '../ActiveEditorContext';
import { useMultiEditor } from '../MultiEditorContext';
import { SelectionContext } from '../../SelectedBlockInfoProvider';
import type { Editor, EditorEvents } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Transaction } from '@tiptap/pm/state';
import { ColumnTypeIdentifier, RowTypesIdentifier } from '../../../grid/reduxStore/table.types';
import { isEligibleForStyling } from '../../Block/Table/useApplyCustomRulesToColumns';
import { isHistoryTransaction } from '@tiptap/pm/history';
import { getSanitizedDataForTableFooter } from '../../../../../utils/getSanitizedDataForTableFooter';
import { selectAllVariablesItems } from '../../../grid/reduxStore/allVariableSlice';
import type { RootState } from '../../../grid/reduxStore/Store';
import { useAppSelector } from '../../../grid/reduxStore/Store';
import { selectContentTableRows } from '../../../grid/reduxStore/editorSlice';
import { debounce } from '../../../../../utils/debounce';
import { getTableBlockExtensionsForEditableCellsInEditor } from '../helpers/config.ts';
import { useAIToken } from '../TipTapBarMenu/AI';
import { isJson } from '../../../../../utils/isJson.ts';
import { useBlockClipboardOptions } from '../../CustomContextMenu/useBlockClipboardOptions';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useIsAIGenerating } from '../hooks/useIsAIGenerating';
import { useComments } from 'providers/CommentsContext';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { createSelector } from 'reselect';
import { selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { ContentType } from 'utils/getContentType';
import { useCrmArrayVariablePaste, useCrmArrayVariableSelect } from '../../Block/Table/crmArrayPaste/useCrmArrayVariablePaste';
import { useCrmCellPropagation } from '../../Block/Table/crmArrayPaste/useCrmCellPropagation';
import { transactionTouchesVariableNode } from '../extensions/variables/variableTransactions';
import { chipNamesIn } from '../../Block/Table/crmArrayPaste/crmChipDocs';
import { perColumnKey } from '../../Block/Table/crmArrayPaste/deriveCrmRows';
import { dropGeneratedRowsForSources, resolveOrphanedSourceRowIds } from '../../Block/Table/tableUtils/crmOrphanFamilies';
import type { CrmRowRefState } from '../../Block/Table/tableUtils/crmOrphanFamilies';
import { isGeneratedRow } from '../../../grid/reduxStore/crmRowMarkers';
import { useInlineImageInsertion } from '../hooks/useInlineImageInsertion';

// Captures the most recent mousedown coordinates so the editor can position the
// cursor at the click location when it mounts. Needs to be module-level because
// the activating click fires before TipTapRenderEditCell mounts.
let lastMouseDownCoords: { left: number; top: number; detail: number } | null = null;
if (typeof document !== 'undefined') {
  document.addEventListener(
    'mousedown',
    (e) => {
      lastMouseDownCoords = { left: e.clientX, top: e.clientY, detail: e.detail };
    },
    true
  );
}

const selectCellValue = createSelector(
  [
    (state: RootState, sectionId: string, blockId: string) => selectContentTableRows(state, sectionId, blockId),
    (_state: RootState, _sectionId: string, _blockId: string, rowId: string) => rowId,
    (_state: RootState, _sectionId: string, _blockId: string, _rowId: string, field: string) => field,
  ],
  (rows, rowId, field) => {
    const row = rows?.find((r) => r.id === rowId);
    return row?.[field] as string | undefined;
  }
);

export const getTableCellEditorId = (id: GridRowId, field: string) => `table_cell_${id}_${field}`;

const getCellPlaceholder = (columnType?: ColumnTypeIdentifier, rowType?: string): string => {
  if (rowType === RowTypesIdentifier.HEADER) return 'Header';
  if (columnType === ColumnTypeIdentifier.TEXT) return 'Title';
  return '';
};

export const TipTapRenderEditCell = ({
  id,
  field,
  hasFocus,
  columnType,
  rowType,
}: {
  id: GridRowId;
  field: string;
  hasFocus: boolean;
  columnType?: ColumnTypeIdentifier;
  rowType?: string;
}) => {
  const { sectionId } = useSectionData();
  const { t } = useTranslation();

  const { handleTableContentChanged } = useTableContentUndoRedo();

  const { getTableData } = useTableManipulation();
  const { activeEditor, setActiveEditor, setIsEditMode } = useActiveEditor();
  const { registerEditor, unregisterEditor } = useMultiEditor();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const allVariables = useAppSelector(selectAllVariablesItems);
  const { commentsEnabled } = useComments();
  const featureFlags = useFeatureFlag([FeatureFlags.commentingOnTable, FeatureFlags.inlineImages]);
  const isCommentingOnTableEnabled = featureFlags[FeatureFlags.commentingOnTable];
  const isInlineImagesEnabled = !!featureFlags[FeatureFlags.inlineImages];
  const { token } = useAIToken();
  const copyAndPasteMenuOptions = useBlockClipboardOptions();
  const disableSuggestions = useAppSelector(selectContentType) === ContentType.SECTION;

  const blockIdRef = useRef<string | null>(selectedBlockIdByWrapper);
  if (hasFocus && selectedBlockIdByWrapper) {
    blockIdRef.current = selectedBlockIdByWrapper;
  }

  const tableData = blockIdRef.current ? getTableData(blockIdRef.current, sectionId) : undefined;
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;

  const rowData = tableData?.rows.find((row) => row.id === id);
  const cellValue = rowData?.[field];
  const documentContent = useMemo(() => (cellValue && isJson(cellValue) ? JSON.parse(cellValue) : cellValue), [cellValue]);

  const reduxCellValue = useAppSelector((state: RootState) => {
    if (!blockIdRef.current) return undefined;
    return selectCellValue(state, sectionId, blockIdRef.current, id as string, field);
  });

  const lastSentContentRef = useRef<string | null>(null);

  const updateTableRow = (row, html, data) => {
    if (row.id !== id) return row;
    if (row.rowType === RowTypesIdentifier.FOOTER) {
      return { ...getSanitizedDataForTableFooter(row, data.columns, html), rowType: RowTypesIdentifier.FOOTER };
    }

    if (!row.crmFieldInstances) {
      return { ...row, [field]: html };
    }

    // A chip deleted from this column leaves its crmFieldInstances entry behind — drop any
    // entry scoped to this column whose template no longer has a matching chip here.
    const remainingKeysForColumn = new Set(
      chipNamesIn(html).flatMap((fieldRef) => (fieldRef.template ? [perColumnKey(field, fieldRef.template)] : []))
    );
    const remainingEntries = Object.entries(row.crmFieldInstances).filter(
      ([key]) => !key.startsWith(`${field}:`) || remainingKeysForColumn.has(key)
    );
    const crmFieldInstances = remainingEntries.length > 0 ? Object.fromEntries(remainingEntries) : undefined;

    return { ...row, [field]: html, crmFieldInstances };
  };

  // Select-all + typing replaces the whole doc, which recreates the paragraph
  // node from scratch and drops data-table-style. Restore it from this cell's
  // row/column type so the cell keeps its header/body/footer styling.
  //
  // setCustomParagraph dispatches its own transaction, which re-enters
  // onUpdate synchronously before this call returns. isRestoringRowStyleRef
  // marks that re-entrant call so it skips redoing the persist logic below —
  // the outer call (this one) already re-reads the editor's corrected state
  // once the fix has been applied and persists exactly once.
  const isRestoringRowStyleRef = useRef(false);
  const restoreRowStyleIfMissing = (editor: Editor, transaction: Transaction) => {
    if (isHistoryTransaction(transaction)) return;

    const cellParagraph = editor.state.doc.firstChild;
    const isStylingEligible = rowType && columnType && isEligibleForStyling(rowType as RowTypesIdentifier, columnType);
    if (isStylingEligible && cellParagraph?.type.name === 'paragraph' && !cellParagraph.attrs['data-table-style']) {
      isRestoringRowStyleRef.current = true;
      editor.commands.setCustomParagraph({ rowType: `table_${rowType.toLowerCase()}_row` });
      isRestoringRowStyleRef.current = false;
    }
  };

  const propagateToGeneratedRows = useCrmCellPropagation({ rowId: String(id), field });
  const propagateRef = useRef(propagateToGeneratedRows);
  propagateRef.current = propagateToGeneratedRows;

  // Re-reads the table fresh when the debounce fires, instead of replaying a snapshot from
  // keystroke time that could be stale by then (e.g. a chip pasted into a sibling column).
  const commitLatestCellEdit = useCallback(
    (json: string) => {
      if (!selectedBlockIdByWrapper || !blockIdRef.current) return;
      const freshTableData = getTableData(blockIdRef.current, sectionId);
      if (!freshTableData) return;

      const stateByRowId = new Map<string, CrmRowRefState>();

      let updatedRows = freshTableData.rows.map((row) => {
        const nextRow = updateTableRow(row, json, freshTableData);
        if (isGeneratedRow(row) || !row.crmFieldInstances) stateByRowId.set(row.id, 'unbound');
        else if (row.id !== id) stateByRowId.set(row.id, 'references');
        else stateByRowId.set(row.id, nextRow.crmFieldInstances ? 'references' : 'orphaned');
        return nextRow;
      });

      const orphanedSourceRowIds = resolveOrphanedSourceRowIds(updatedRows, (row) => stateByRowId.get(row.id) ?? 'unbound');
      updatedRows = dropGeneratedRowsForSources(updatedRows, orphanedSourceRowIds);

      const rowsToCommit = propagateRef.current(updatedRows);

      handleTableContentChanged(selectedBlockIdByWrapper, sectionId, freshTableData, {
        ...freshTableData,
        rows: rowsToCommit,
      });
    },
    [selectedBlockIdByWrapper, sectionId, getTableData, handleTableContentChanged]
  );
  const debouncedBlockContentChangedHandler = useCallback(debounce(commitLatestCellEdit, 1000), [commitLatestCellEdit]);

  // Unmounting destroys this cell's TipTap instance — flush any pending debounced edit first.
  useEffect(() => {
    return () => {
      debouncedBlockContentChangedHandler.flush();
    };
  }, [debouncedBlockContentChangedHandler]);

  const updateEditorData = useCallback(
    (editor: Editor, transaction: Transaction) => {
      if (isRestoringRowStyleRef.current) return;

      restoreRowStyleIfMissing(editor, transaction);

      const json = JSON.stringify(editor.getJSON());

      if (tableDataRef.current && selectedBlockIdByWrapper) {
        lastSentContentRef.current = json;

        debouncedBlockContentChangedHandler(json);

        // Chip inserts/deletes flush synchronously — the re-derivation that follows would
        // otherwise be overwritten by the still-armed debounced commit. Typing keeps its debounce.
        if (transactionTouchesVariableNode(transaction)) {
          debouncedBlockContentChangedHandler.flush();
        }
      }
    },
    [debouncedBlockContentChangedHandler, selectedBlockIdByWrapper, id, field]
  );

  const editor = useEditor(
    {
      content: documentContent ?? '',
      extensions: [
        ...getTableBlockExtensionsForEditableCellsInEditor({
          variables: allVariables ?? [],
          jwtToken: token,
          menuItems: copyAndPasteMenuOptions,
          disableSuggestions,
        }),
      ],
      editorProps: { attributes: { class: 'py-mui-tiptap-input' } },
      onUpdate: ({ editor, transaction }: EditorEvents['update']) => updateEditorData(editor, transaction),
      onCreate: ({ editor }: EditorEvents['create']) => {
        editor.storage.tiptapAiExtension.isTableCellEditor = true;
      },
    },
    [token]
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed || !reduxCellValue) return;

    const currentJSON = JSON.stringify(editor.getJSON());
    if (currentJSON !== reduxCellValue) {
      lastSentContentRef.current = null;

      const { from, to } = editor.state.selection;
      const parsed = isJson(reduxCellValue) ? JSON.parse(reduxCellValue) : reduxCellValue;
      editor.commands.setContent(parsed, false);

      const docSize = editor.state.doc.content.size;
      const safeFrom = Math.min(from, Math.max(0, docSize - 1));
      const safeTo = Math.min(to, Math.max(0, docSize - 1));

      requestAnimationFrame(() => {
        if (!editor.isDestroyed) {
          editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
        }
      });
    }
  }, [editor, reduxCellValue]);

  const focusAtClickPosition = (editorInstance: Editor) => {
    requestAnimationFrame(() => {
      if (editorInstance.isDestroyed) return;
      if (lastMouseDownCoords && lastMouseDownCoords.detail >= 2) return;
      if (lastMouseDownCoords) {
        const { left, top } = lastMouseDownCoords;
        const range = document.caretRangeFromPoint?.(left, top);
        if (range && editorInstance.view.dom.contains(range.startContainer)) {
          try {
            const pos = editorInstance.view.posAtDOM(range.startContainer, range.startOffset);
            editorInstance.commands.focus(pos);
            return;
          } catch {
            /* fall through */
          }
        }
        const resolved = editorInstance.view.posAtCoords({ left, top });
        if (resolved !== null) {
          editorInstance.commands.focus(resolved.pos);
          return;
        }
      }
      editorInstance.commands.focus();
    });
  };

  const handleCrmArrayPaste = useCrmArrayVariablePaste({
    editor,
    rowId: String(id),
    field,
    rowType,
    sectionId,
    getBlockId: () => blockIdRef.current,
    cancelPendingCellCommit: () => debouncedBlockContentChangedHandler.cancel(),
  });

  const handleArrayVariableInserted = useCrmArrayVariableSelect({
    editor,
    rowId: String(id),
    field,
    rowType,
    sectionId,
    getBlockId: () => blockIdRef.current,
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed || !editor.storage.variable) return;
    editor.storage.variable.onArrayVariableInserted = handleArrayVariableInserted;
    return () => {
      if (!editor.isDestroyed && editor.storage.variable) {
        editor.storage.variable.onArrayVariableInserted = null;
      }
    };
  }, [editor, handleArrayVariableInserted]);

  useEffect(() => {
    if (hasFocus && editor && activeEditor !== editor) {
      if (commentsEnabled && !isCommentingOnTableEnabled) {
        return;
      }
      setIsEditMode(true);
      setActiveEditor(editor);
      focusAtClickPosition(editor);
    }
  }, [editor, activeEditor, setActiveEditor, hasFocus, setIsEditMode, commentsEnabled, isCommentingOnTableEnabled]);

  useEffect(() => {
    if (editor) {
      const editorId = getTableCellEditorId(id, field);
      registerEditor(editorId, editor);
      return () => {
        unregisterEditor(editorId);
      };
    }
  }, [editor, id, field, registerEditor, unregisterEditor]);

  useInlineImageInsertion(editor, isInlineImagesEnabled);

  const isAIGenerating = useIsAIGenerating(editor);

  if (!blockIdRef.current || !rowData) {
    return null;
  }

  if (!editor) {
    return null;
  }

  if (isAIGenerating) {
    return (
      <Box className="editable-cell-wrapper" sx={{ width: '100%', height: '100%' }}>
        <Skeleton variant="rounded" width="100%" height="100%" animation="wave" />
      </Box>
    );
  }

  const cellPlaceholder = getCellPlaceholder(columnType, rowType);
  const fallbackTooltipTitle = rowData.crmGroupUnrelatedFallback ? t('document.grid.table.crm_group_unrelated_fallback_tooltip') : '';

  return (
    <Tooltip title={fallbackTooltipTitle} placement="bottom">
      <div
        className="editable-cell-wrapper"
        data-column-type={columnType}
        data-row-type={rowType}
        data-placeholder={editor.isEmpty && cellPlaceholder ? cellPlaceholder : undefined}
        onPasteCapture={handleCrmArrayPaste}
      >
        <EditorContent editor={editor} />
      </div>
    </Tooltip>
  );
};
